import React, { useId, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { AppIcon } from './LucideIcon';
import { useAssetDocuments } from '../hooks/useAssetDocuments';
import type { AssetDocument } from '../types';
import './documents-section.css';

interface DocumentsSectionProps {
  /** The drawing's plant, whose register the tag is looked up in. */
  domainId: string;
  /** The element's tag as it is saved, not as it is being typed. */
  tag: string;
}

const lineStyle: React.CSSProperties = { fontSize: 11, color: 'var(--raven-text-secondary)' };

function DocumentRow({ assetDocument }: { assetDocument: AssetDocument }) {
  const title = assetDocument.display_title || assetDocument.file_name;
  return (
    <li style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
      {assetDocument.category && (
        <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, color: 'var(--raven-text-secondary)' }}>
          {assetDocument.category}
        </span>
      )}
      <span
        title={title}
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'var(--raven-text-primary)',
        }}
      >
        {title}
      </span>
      <button
        type="button"
        aria-label={`Open ${title} in a new tab`}
        title="Open in a new tab"
        onClick={() => window.open(assetDocument.open_url, '_blank', 'noopener')}
        style={{
          flexShrink: 0,
          padding: '0 6px',
          fontSize: 12,
          color: 'var(--raven-primary-main)',
          background: 'transparent',
          border: '1px solid var(--raven-divider)',
          borderRadius: 4,
          cursor: 'pointer',
        }}
      >
        ↗
      </button>
    </li>
  );
}

/**
 * The documents linked to the register asset that carries an element's tag in its
 * plant. Read-only: each row opens the document on the copilot web app, where links
 * are edited and approved.
 *
 * DECISION(2026-11-03, Abhishek): the card opens collapsed, its header carrying the count.
 * The list is reference material beside the fields an SME edits; the count says whether
 * opening it is worth a click.
 */
const DocumentsSection: React.FC<DocumentsSectionProps> = ({ domainId, tag }) => {
  const [expanded, setExpanded] = useState(false);
  const bodyId = useId();
  const { assetMatchQuery, documentsQuery } = useAssetDocuments(domainId, tag);
  const assetMatch = assetMatchQuery.data;

  // Null until known. A tag that names no asset, or several, lists nothing.
  const documents: AssetDocument[] | null =
    assetMatch === undefined ? null
    : assetMatch.match === 'one' ? documentsQuery.data ?? null
    : [];

  const failed = assetMatchQuery.isError || documentsQuery.isError;

  let body: React.ReactNode;
  if (failed) {
    body = <div style={{ ...lineStyle, color: 'var(--raven-error-text)' }}>The documents could not be loaded.</div>;
  } else if (documents === null) {
    body = <div style={lineStyle}>Loading documents…</div>;
  } else if (documents.length === 0) {
    body = (
      <>
        <div style={lineStyle}>No documents linked to this tag.</div>
        {assetMatch?.match === 'several' && (
          <div style={lineStyle}>Several register assets match this tag.</div>
        )}
      </>
    );
  } else {
    const firstCommonIndex = documents.findIndex((assetDocument) => assetDocument.is_common);
    // DECISION(2026-11-04, Abhishek): one divider sits before the first plant-common document, only when the asset also has documents of its own.
    // The backend serves the asset's own documents first, so the first `is_common` row is the boundary; a divider above an all-common list, or below an all-own list, would separate nothing.
    const plantDocumentsDividerIndex = firstCommonIndex > 0 ? firstCommonIndex : null;
    body = (
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {documents.map((assetDocument, index) => (
          <React.Fragment key={`${assetDocument.source_file_id}:${assetDocument.evidence_id}`}>
            {index === plantDocumentsDividerIndex && (
              <li role="separator" aria-label="Plant documents" className="pnid-documents-section__divider" />
            )}
            <DocumentRow assetDocument={assetDocument} />
          </React.Fragment>
        ))}
      </ul>
    );
  }

  return (
    <section
      aria-label="Documents"
      style={{
        background: 'var(--raven-surface-tier2)',
        border: '1px solid var(--raven-divider)',
        borderRadius: 6,
      }}
    >
      <button
        type="button"
        className="pnid-documents-section__toggle"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={() => setExpanded((wasExpanded) => !wasExpanded)}
      >
        <span>{documents && !failed ? `Documents (${documents.length})` : 'Documents'}</span>
        <AppIcon icon={expanded ? ChevronDown : ChevronRight} size={14} className="pnid-documents-section__caret" />
      </button>
      {expanded && (
        <div id={bodyId} style={{ padding: 10, borderTop: '1px solid var(--raven-divider)' }}>
          {body}
        </div>
      )}
    </section>
  );
};

export default DocumentsSection;
